import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0B1A]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-3xl font-bold text-white mb-8">Privacy Policy</h1>
        
        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="mb-4">
              This Privacy Policy describes how UnifyAds ("we," "our," or "us") collects, uses, stores, shares, and protects your information when you use our website, services, and applications (collectively, the "Services"). By using our Services, you agree to the collection and use of information in accordance with this Privacy Policy.
            </p>
            <p className="mb-4">
              This policy applies to all information collected through our Services, as well as any related services, sales, marketing, or events.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white">2.1 Personal Information</h3>
              <p className="mb-2">We collect several types of information from and about users of our Services, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Identity Information (name, email address, phone number, postal address)</li>
                <li>Account Credentials (username, password, security questions)</li>
                <li>Business Information (company name, job title, business address)</li>
                <li>Financial Information (billing address, payment method details)</li>
                <li>Technical Information (IP address, browser type, device information)</li>
                <li>Usage Information (interaction with our Services, preferences)</li>
              </ul>

              <h3 className="text-lg font-medium text-white mt-6">2.2 Information from Third-Party Platforms</h3>
              <p className="mb-2">When you connect third-party advertising platforms, we may collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Advertising account information and credentials</li>
                <li>Campaign performance data and metrics</li>
                <li>Audience and targeting information</li>
                <li>Ad creative and content data</li>
                <li>Billing and spending information</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use the information we collect for various purposes, including:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Providing and maintaining our Services</li>
              <li>Processing your transactions and payments</li>
              <li>Analyzing and optimizing advertising campaigns</li>
              <li>Communicating with you about our Services</li>
              <li>Protecting against fraudulent or illegal activity</li>
              <li>Complying with legal obligations</li>
              <li>Improving and personalizing our Services</li>
              <li>Providing customer support and responding to inquiries</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Legal Basis for Processing</h2>
            <p className="mb-4">We process your information under the following legal bases:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Performance of a contract when we provide you with our Services</li>
              <li>Your consent when you agree to the processing of your data</li>
              <li>Legitimate interests in operating and improving our business</li>
              <li>Compliance with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Data Sharing and Disclosure</h2>
            <p className="mb-4">We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Service providers and business partners who assist in operating our Services</li>
              <li>Advertising platforms when you connect your accounts</li>
              <li>Law enforcement or regulatory authorities when required by law</li>
              <li>Professional advisors (lawyers, accountants, auditors)</li>
            </ul>
            <p className="mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Data Security</h2>
            <p className="mb-4">
              We implement appropriate technical and organizational security measures to protect your information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and audits</li>
              <li>Access controls and authentication measures</li>
              <li>Employee training on data protection</li>
              <li>Incident response procedures</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Your Privacy Rights</h2>
            <p className="mb-4">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to data processing</li>
              <li>Request data portability</li>
              <li>Withdraw consent at any time</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. International Data Transfers</h2>
            <p className="mb-4">
              We may transfer your information to countries outside your residence country. When we do, we ensure appropriate safeguards are in place, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Standard contractual clauses</li>
              <li>Data processing agreements</li>
              <li>Adequacy decisions where applicable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Data Retention</h2>
            <p className="mb-4">
              We retain your information for as long as necessary to provide our Services and fulfill the purposes outlined in this Privacy Policy. When we no longer need your information, we securely delete or anonymize it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Cookies and Tracking Technologies</h2>
            <p className="mb-4">
              We use cookies and similar tracking technologies to improve your experience and collect usage data. You can control these through your browser settings. We use these technologies for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Essential website functionality</li>
              <li>Performance and analytics</li>
              <li>Personalization</li>
              <li>Security and fraud prevention</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Children's Privacy</h2>
            <p className="mb-4">
              Our Services are not intended for individuals under 16 years of age. We do not knowingly collect or process personal information from children. If we become aware that we have collected personal information from a child without parental consent, we will take steps to delete that information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">12. Changes to This Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of our Services after such modifications constitutes your acknowledgment of the modified Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">13. Contact Information</h2>
            <p className="mb-4">
              If you have questions about this Privacy Policy or our privacy practices, please contact our Data Protection Officer at:
            </p>
            <div className="bg-[#151632]/50 backdrop-blur-sm p-6 rounded-xl border border-[#1E2048]">
              <p className="mb-2">UnifyAds</p>
              <p className="mb-2">Email: privacy@unifyads.io</p>
              <p className="mb-2">Address: 123 Privacy Street, Tech City, TC 12345</p>
              <p>Phone: +1 (555) 123-4567</p>
            </div>
          </section>

          <section className="pt-8">
            <p className="text-sm text-gray-400">
              Last updated: March 15, 2024
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;